import { gql } from '@apollo/client'
import client from '../utils/client'
import { MutationHandlerError } from '../utils/errors'

export type RestaurantMessageFormat = 'PAPER' | 'POSTCARD' | 'EMAIL'

// Erstanschreiben mit Gemeinden und Tourrechnung oder nur Betreff und Text.
export type RestaurantMessageContent = 'INITIAL' | 'FREE_TEXT'

export type RestaurantMessageAttachment = 'DV' | 'HAGB'

// Je nach Format nur pdf (PAPER), png mit backText (POSTCARD), beide base64, oder html mit subject (EMAIL).
export type RestaurantMessage = {
	pdf: string | null
	png: string | null
	backText: string | null
	html: string | null
	subject: string | null
}

type CreateRestaurantMessageResult =
	| { success: true; message: RestaurantMessage; errors: null }
	| { success: false; message: undefined; errors: MutationHandlerError }

const CREATE_RESTAURANT_MESSAGE_MUTATION = gql`
	mutation CreateRestaurantMessage(
		$settlementIds: [ID!]!
		$slug: String
		$restaurantName: String
		$format: RestaurantMessageFormat!
		$intro: String
		$greeting: String
		$content: RestaurantMessageContent
		$subject: String
		$attachments: [RestaurantMessageAttachment!]
	) {
		createRestaurantMessage(
			settlementIds: $settlementIds
			slug: $slug
			restaurantName: $restaurantName
			format: $format
			intro: $intro
			greeting: $greeting
			content: $content
			subject: $subject
			attachments: $attachments
		) {
			pdf
			png
			backText
			html
			subject
		}
	}
`

// Brief und Postkarte eines Orts an höchstens ein Restaurant, ohne es mit restaurantName an die
// Testadresse; die Mail nennt jede Gemeinde in settlementIds.
const createRestaurantMessage = async ({
	settlementIds,
	slug,
	restaurantName,
	format,
	intro,
	greeting,
	content,
	subject,
	attachments,
}: {
	settlementIds: string[]
	slug?: string
	restaurantName?: string
	format: RestaurantMessageFormat
	intro?: string
	// Leer gilt die übliche Anrede.
	greeting?: string
	content?: RestaurantMessageContent
	subject?: string
	attachments?: RestaurantMessageAttachment[]
}): Promise<CreateRestaurantMessageResult> => {
	try {
		const { data, errors } = await client.mutate({
			mutation: CREATE_RESTAURANT_MESSAGE_MUTATION,
			variables: {
				settlementIds,
				slug,
				restaurantName,
				format,
				intro,
				greeting,
				content,
				subject,
				attachments,
			},
			errorPolicy: 'all',
		})

		return errors
			? { success: false, message: undefined, errors }
			: { success: true, message: data.createRestaurantMessage, errors: null }
	} catch (e) {
		if (e && e.networkError) return { success: false, message: undefined, errors: 'NETWORK_ERROR' }
		throw e
	}
}

export default createRestaurantMessage
